/**
 * Task Service
 * Real-time Firestore CRUD operations for task management
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { api } from '../api';
import { getCompanyId } from '../tenantContext';
import type {
  Task,
  CreateTaskInput,
  UpdateTaskInput,
  TaskFilters,
  TaskStats,
} from '../../types/task';

/**
 * Get tasks collection reference for current tenant
 */
function getTasksCollection() {
  const companyId = getCompanyId();
  return collection(db, `companies/${companyId}/tasks`);
}

/**
 * Convert Firestore document to Task object
 */
function firestoreToTask(doc: any): Task {
  const data = doc.data();
  return {
    id: doc.id,
    companyId: data.companyId,
    title: data.title,
    description: data.description,
    status: data.status,
    priority: data.priority,
    wellId: data.wellId,
    wellName: data.wellName,
    ticketId: data.ticketId,
    assignedTo: data.assignedTo,
    assignedToName: data.assignedToName,
    assignedBy: data.assignedBy,
    assignedByName: data.assignedByName,
    dueDate: data.dueDate,
    startedAt: data.startedAt,
    completedAt: data.completedAt,
    createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
    notes: data.notes,
    tags: data.tags || [],
    estimatedHours: data.estimatedHours,
    actualHours: data.actualHours,
    completionNotes: data.completionNotes,
    completedBy: data.completedBy,
    completedByName: data.completedByName,
    createdByName: data.createdByName,
    lastModifiedBy: data.lastModifiedBy,
    lastModifiedByName: data.lastModifiedByName,
  };
}

/**
 * Convert Task object to Firestore document
 */
function taskToFirestore(task: Partial<Task>) {
  const data: any = { ...task };
  
  // Convert ISO date strings to Firestore Timestamps
  if (data.createdAt && typeof data.createdAt === 'string') {
    data.createdAt = Timestamp.fromDate(new Date(data.createdAt));
  }
  if (data.updatedAt && typeof data.updatedAt === 'string') {
    data.updatedAt = Timestamp.fromDate(new Date(data.updatedAt));
  }
  
  return data;
}

/**
 * Create a new task
 */
export async function createTask(
  input: CreateTaskInput,
  currentUserId: string,
  currentUserName: string
): Promise<Task> {
  const companyId = getCompanyId();
  const now = new Date().toISOString();
  
  const taskData: Omit<Task, 'id'> = {
    companyId,
    title: input.title,
    description: input.description,
    status: 'todo',
    priority: input.priority,
    wellId: input.wellId,
    wellName: input.wellName,
    ticketId: input.ticketId,
    assignedTo: input.assignedTo,
    assignedToName: input.assignedToName,
    assignedBy: currentUserId,
    assignedByName: currentUserName,
    dueDate: input.dueDate,
    createdAt: now,
    updatedAt: now,
    notes: input.notes,
    tags: input.tags || [],
    estimatedHours: input.estimatedHours,
    createdByName: currentUserName,
    lastModifiedBy: currentUserId,
    lastModifiedByName: currentUserName,
  };
  
  const docRef = await addDoc(getTasksCollection(), taskToFirestore(taskData));
  
  // Create notification for the assigned user if it's not the creator
  if (input.assignedTo && input.assignedTo !== currentUserId) {
    try {
      await api.createNotification(companyId, {
        userId: input.assignedTo,
        title: 'New Task Assigned',
        message: `${currentUserName} assigned you a task: ${input.title}`,
        type: 'info',
        link: `/tasks?id=${docRef.id}`,
        metadata: {
          taskId: docRef.id,
          wellId: input.wellId,
          priority: input.priority
        }
      });
    } catch (error) {
      console.error('Failed to create task notification:', error);
    }
  }

  return {
    ...taskData,
    id: docRef.id,
  };
}

/**
 * Get a single task by ID
 */
export async function getTask(taskId: string): Promise<Task | null> {
  const companyId = getCompanyId();
  const docRef = doc(db, `companies/${companyId}/tasks`, taskId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return firestoreToTask(docSnap);
}

/**
 * Get all tasks with optional filters
 */
export async function getTasks(filters?: TaskFilters): Promise<Task[]> {
  const tasksRef = getTasksCollection();
  const constraints: QueryConstraint[] = [];
  
  // Apply filters
  if (filters?.status && filters.status !== 'all') {
    constraints.push(where('status', '==', filters.status));
  }
  
  if (filters?.priority && filters.priority !== 'all') {
    constraints.push(where('priority', '==', filters.priority));
  }
  
  if (filters?.assignedTo && filters.assignedTo !== 'all') {
    constraints.push(where('assignedTo', '==', filters.assignedTo));
  }
  
  if (filters?.wellId) {
    constraints.push(where('wellId', '==', filters.wellId));
  }
  
  if (filters?.ticketId) {
    constraints.push(where('ticketId', '==', filters.ticketId));
  }
  
  // Default sort by priority and due date
  constraints.push(orderBy('priority', 'desc'));
  constraints.push(orderBy('createdAt', 'desc'));
  
  const q = query(tasksRef, ...constraints);
  const querySnapshot = await getDocs(q);
  
  let tasks = querySnapshot.docs.map(firestoreToTask);
  
  // Apply client-side filters
  if (filters?.searchQuery) {
    const query = filters.searchQuery.toLowerCase();
    tasks = tasks.filter(
      task =>
        task.title.toLowerCase().includes(query) ||
        task.description.toLowerCase().includes(query) ||
        task.wellName?.toLowerCase().includes(query) ||
        task.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }
  
  if (filters?.tags && filters.tags.length > 0) {
    tasks = tasks.filter(task =>
      filters.tags!.some(tag => task.tags?.includes(tag))
    );
  }
  
  // Sort by status priority (in_progress > todo > completed > cancelled)
  const statusOrder = { in_progress: 0, todo: 1, completed: 2, cancelled: 3 };
  tasks.sort((a, b) => {
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    
    // Then by priority
    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    
    // Finally by due date
    if (a.dueDate && b.dueDate) {
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    }
    return 0;
  });
  
  return tasks;
}

/**
 * Update a task
 */
export async function updateTask(
  taskId: string,
  updates: UpdateTaskInput,
  currentUserId: string,
  currentUserName: string
): Promise<void> {
  const companyId = getCompanyId();
  const docRef = doc(db, `companies/${companyId}/tasks`, taskId);
  
  const updateData: any = {
    ...updates,
    updatedAt: Timestamp.fromDate(new Date()),
    lastModifiedBy: currentUserId,
    lastModifiedByName: currentUserName,
  };
  
  // Track when task is started
  if (updates.status === 'in_progress') {
    const task = await getTask(taskId);
    if (task && !task.startedAt) {
      updateData.startedAt = new Date().toISOString();
    }
  }
  
  // Track when task is completed
  if (updates.status === 'completed') {
    updateData.completedAt = new Date().toISOString();
    updateData.completedBy = currentUserId;
    updateData.completedByName = currentUserName;
  }
  
  await updateDoc(docRef, updateData);
}

/**
 * Update task status (convenience method)
 */
export async function updateTaskStatus(
  taskId: string,
  status: Task['status'],
  currentUserId: string,
  currentUserName: string,
  completionNotes?: string
): Promise<void> {
  await updateTask(
    taskId,
    { status, completionNotes },
    currentUserId,
    currentUserName
  );
}

/**
 * Delete a task
 */
export async function deleteTask(taskId: string): Promise<void> {
  const companyId = getCompanyId();
  const docRef = doc(db, `companies/${companyId}/tasks`, taskId);
  await deleteDoc(docRef);
}

/**
 * Subscribe to real-time task updates
 */
export function subscribeToTasks(
  callback: (tasks: Task[]) => void,
  filters?: TaskFilters
): () => void {
  const tasksRef = getTasksCollection();
  const constraints: QueryConstraint[] = [];
  
  if (filters?.status && filters.status !== 'all') {
    constraints.push(where('status', '==', filters.status));
  }
  
  if (filters?.assignedTo && filters.assignedTo !== 'all') {
    constraints.push(where('assignedTo', '==', filters.assignedTo));
  }
  
  if (filters?.wellId) {
    constraints.push(where('wellId', '==', filters.wellId));
  }
  
  constraints.push(orderBy('createdAt', 'desc'));
  
  const q = query(tasksRef, ...constraints);
  
  return onSnapshot(q, (snapshot) => {
    const tasks = snapshot.docs.map(firestoreToTask);
    callback(tasks);
  });
}

/**
 * Get task statistics
 */
export async function getTaskStats(userId?: string): Promise<TaskStats> {
  const tasks = await getTasks();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  const stats: TaskStats = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'todo').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    cancelled: tasks.filter(t => t.status === 'cancelled').length,
    overdue: tasks.filter(
      t => t.dueDate && new Date(t.dueDate) < now && t.status !== 'completed'
    ).length,
    dueToday: tasks.filter(
      t => t.dueDate && new Date(t.dueDate) <= today && t.status !== 'completed'
    ).length,
    dueThisWeek: tasks.filter(
      t => t.dueDate && new Date(t.dueDate) <= weekFromNow && t.status !== 'completed'
    ).length,
    highPriority: tasks.filter(
      t => (t.priority === 'high' || t.priority === 'urgent') && t.status !== 'completed'
    ).length,
    assignedToMe: userId ? tasks.filter(t => t.assignedTo === userId).length : 0,
    assignedByMe: userId ? tasks.filter(t => t.assignedBy === userId).length : 0,
  };
  
  return stats;
}

export const taskService = {
  createTask,
  getTask,
  getTasks,
  updateTask,
  updateTaskStatus,
  deleteTask,
  subscribeToTasks,
  getTaskStats,
};
